import { QuestionKey, RangeValue, TimeUnit } from '@/clients/Communication/Questionnaire.dto';
import { isCalendarValid, useGetNylasConnectionStatusQuery } from '@/features/nylas-calendar-manage-connection-modal';
import {
  getShiftLabel,
  getWorkdayLabel,
  shiftOptions,
  workdayOptions,
  WorkdayType,
} from '@/shared/legacy/contexts/DialogContext/modals/EnrichVacancyModal/utils/EnrichVacancyModal.constants';
import { Button, Checkbox, DialogV2, Loader, Typography } from '@recruit-robin/robin-theme/web-components';
import { ChevronLeft, Star_06 } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useState } from 'react';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { InputRequirement } from '../../CandidateQuestionnaireScreen/components/InputRequirement';
import { RangeInputRequirement } from '../../CandidateQuestionnaireScreen/components/RangeInputRequirement';
import { SelectRequirement } from '../../CandidateQuestionnaireScreen/components/SelectRequirement';
import { useQuestion } from '../../CandidateQuestionnaireScreen/hooks/useQuestion';
import { LoadingRjCommDialog } from '../LoadingRjCommDialog/LoadingRjCommDialog';
import { AutoSendInterviewInvite } from './AutoSendInterviewInvite';
import { useStyles } from './QuestionnairePreviewDialog.styles';
import { QuestionnairePreviewDialogProps } from './QuestionnairePreviewDialog.types';
import { useQuestionnaire } from './QuestionnairePreviewDialog.useQuestionnaire';
import { QuestionnaireQuestionWrapper } from './QuestionnaireQuestionWrapper';

export const QuestionnairePreviewDialog = ({
  isVisible,
  onClose,
  onConfirm,
  onGoBack,
  isLoading = false,
  dontShowQuestionnairePreview = false,
  setDontShowQuestionnairePreview,
  candidateEsPersonId,
  jobId,
  selectedLanguage,
}: QuestionnairePreviewDialogProps) => {
  const classes = useStyles({});
  const { dictionary } = useContext(LocalizationContext);
  const {
    questions,
    questionnaireState,
    isLoading: isLoadingQuestionnairePreview,
    updateQuestionnaireRequirements,
    updateQuestionnaireFieldUnit,
    updateQuestionnaireRequired,
    shouldShowQuestion,
    pendingReview,
  } = useQuestionnaire({
    esPersonId: candidateEsPersonId,
    jobId,
    selectedLanguage,
  });
  const { getTimeUnitFrequency } = useQuestion();
  const nylasConnectionStatusQuery = useGetNylasConnectionStatusQuery();
  const [isAutoSendInterviewInviteEnabled, setIsAutoSendInterviewInviteEnabled] = useState(true);

  if (isLoadingQuestionnairePreview || !questions) {
    return <LoadingRjCommDialog isVisible={true} title={dictionary.loadingQualificationQuestionary} />;
  }

  return (
    <DialogV2.Root isVisible={isVisible} customCss={classes.dialog}>
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        <DialogV2.Close onRequestClose={onClose} />
        <DialogV2.Header customCss={classes.title}>
          <Star_06 size={24} color={(c) => c.primary.fuchsia[40]} />
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
            {dictionary.reviewQualificationQuestionary}
          </Typography>
          <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[70]}>
            {dontShowQuestionnairePreview === true && pendingReview === true && (
              <>{dictionary.jobUpdatedReviewRequired} </>
            )}
            {dictionary.pleaseReviewTheQualificationQuestions}
          </Typography>
        </DialogV2.Header>

        <DialogV2.Body customCss={classes.body}>
          <div css={classes.questionsList}>
            {shouldShowQuestion(QuestionKey.CERTIFICATES_AND_LICENSES) && (
              <QuestionnaireQuestionWrapper
                question={questions[QuestionKey.CERTIFICATES_AND_LICENSES]}
                isRequired={!!questions[QuestionKey.CERTIFICATES_AND_LICENSES].is_required}
                onToggleRequired={(value) =>
                  updateQuestionnaireRequired(questions[QuestionKey.CERTIFICATES_AND_LICENSES].question_key, value)
                }>
                <InputRequirement
                  value={questions[QuestionKey.CERTIFICATES_AND_LICENSES].job_requirements as string[]}
                  onChange={(value) =>
                    updateQuestionnaireRequirements(
                      questions[QuestionKey.CERTIFICATES_AND_LICENSES].question_key,
                      value,
                    )
                  }
                  placeholder={dictionary.certificationsPlaceholder}
                />
              </QuestionnaireQuestionWrapper>
            )}

            {shouldShowQuestion(QuestionKey.SALARY) && (
              <QuestionnaireQuestionWrapper
                question={questions[QuestionKey.SALARY]}
                isRequired={!!questions[QuestionKey.SALARY].is_required}
                onToggleRequired={(value) =>
                  updateQuestionnaireRequired(questions[QuestionKey.SALARY].question_key, value)
                }>
                <RangeInputRequirement
                  value={questions[QuestionKey.SALARY].job_requirements as RangeValue}
                  onChange={(value) =>
                    updateQuestionnaireRequirements(questions[QuestionKey.SALARY].question_key, value)
                  }
                  unit={questions[QuestionKey.SALARY].unit || TimeUnit.MONTH}
                  onUnitChange={(value) => updateQuestionnaireFieldUnit(QuestionKey.SALARY, value)}
                  unitOptions={[
                    { label: getTimeUnitFrequency(TimeUnit.HOUR), value: TimeUnit.HOUR },
                    { label: getTimeUnitFrequency(TimeUnit.WEEK), value: TimeUnit.WEEK },
                    { label: getTimeUnitFrequency(TimeUnit.MONTH), value: TimeUnit.MONTH },
                    { label: getTimeUnitFrequency(TimeUnit.YEAR), value: TimeUnit.YEAR },
                  ]}
                />
              </QuestionnaireQuestionWrapper>
            )}

            {shouldShowQuestion(QuestionKey.SHIFTS) && (
              <QuestionnaireQuestionWrapper
                question={questions[QuestionKey.SHIFTS]}
                isRequired={!!questions[QuestionKey.SHIFTS].is_required}
                onToggleRequired={(value) =>
                  updateQuestionnaireRequired(questions[QuestionKey.SHIFTS].question_key, value)
                }>
                <SelectRequirement
                  value={(questions[QuestionKey.SHIFTS].job_requirements as string[]) ?? []}
                  onChange={(value) =>
                    updateQuestionnaireRequirements(questions[QuestionKey.SHIFTS].question_key, value)
                  }
                  placeholder={dictionary.shiftPlaceholder}
                  options={shiftOptions}
                  getLabel={getShiftLabel}
                />
              </QuestionnaireQuestionWrapper>
            )}

            {shouldShowQuestion(QuestionKey.AVAILABLE_DAYS) && (
              <QuestionnaireQuestionWrapper
                question={questions[QuestionKey.AVAILABLE_DAYS]}
                isRequired={!!questions[QuestionKey.AVAILABLE_DAYS].is_required}
                onToggleRequired={(value) =>
                  updateQuestionnaireRequired(questions[QuestionKey.AVAILABLE_DAYS].question_key, value)
                }>
                <SelectRequirement
                  value={(questions[QuestionKey.AVAILABLE_DAYS].job_requirements as string[]) ?? []}
                  onChange={(value) =>
                    updateQuestionnaireRequirements(questions[QuestionKey.AVAILABLE_DAYS].question_key, value)
                  }
                  placeholder={dictionary.workdaysPlaceholder}
                  options={workdayOptions}
                  getLabel={(value, language) => getWorkdayLabel(value as WorkdayType, language)}
                />
              </QuestionnaireQuestionWrapper>
            )}

            {shouldShowQuestion(QuestionKey.WORKING_HOURS_PER_WEEK) && (
              <QuestionnaireQuestionWrapper
                question={questions[QuestionKey.WORKING_HOURS_PER_WEEK]}
                isRequired={!!questions[QuestionKey.WORKING_HOURS_PER_WEEK].is_required}
                onToggleRequired={(value) =>
                  updateQuestionnaireRequired(questions[QuestionKey.WORKING_HOURS_PER_WEEK].question_key, value)
                }>
                <RangeInputRequirement
                  value={questions[QuestionKey.WORKING_HOURS_PER_WEEK].job_requirements as RangeValue}
                  onChange={(value) =>
                    updateQuestionnaireRequirements(questions[QuestionKey.WORKING_HOURS_PER_WEEK].question_key, value)
                  }
                  unit={questions[QuestionKey.WORKING_HOURS_PER_WEEK].unit || TimeUnit.WEEK}
                  isFixedUnit
                />
              </QuestionnaireQuestionWrapper>
            )}

            {shouldShowQuestion(QuestionKey.LIMITATIONS_AND_PREFERENCES) && (
              <QuestionnaireQuestionWrapper
                question={questions[QuestionKey.LIMITATIONS_AND_PREFERENCES]}
                isRequired={!!questions[QuestionKey.LIMITATIONS_AND_PREFERENCES].is_required}
                onToggleRequired={(value) =>
                  updateQuestionnaireRequired(questions[QuestionKey.LIMITATIONS_AND_PREFERENCES].question_key, value)
                }>
                <InputRequirement
                  value={(questions[QuestionKey.LIMITATIONS_AND_PREFERENCES].job_requirements as string[]) ?? []}
                  onChange={(value) =>
                    updateQuestionnaireRequirements(
                      questions[QuestionKey.LIMITATIONS_AND_PREFERENCES].question_key,
                      value,
                    )
                  }
                />
              </QuestionnaireQuestionWrapper>
            )}

            {shouldShowQuestion(QuestionKey.LANGUAGES) && (
              <QuestionnaireQuestionWrapper
                question={questions[QuestionKey.LANGUAGES]}
                isRequired={!!questions[QuestionKey.LANGUAGES].is_required}
                onToggleRequired={(value) =>
                  updateQuestionnaireRequired(questions[QuestionKey.LANGUAGES].question_key, value)
                }>
                <InputRequirement
                  value={(questions[QuestionKey.LANGUAGES].job_requirements as string[]) ?? []}
                  onChange={(value) =>
                    updateQuestionnaireRequirements(questions[QuestionKey.LANGUAGES].question_key, value)
                  }
                  placeholder={dictionary.languagesPlaceholder}
                />
              </QuestionnaireQuestionWrapper>
            )}
          </div>

          {isCalendarValid(nylasConnectionStatusQuery.data) && (
            <AutoSendInterviewInvite
              isAutoSendInterviewInviteEnabled={isAutoSendInterviewInviteEnabled}
              onToggle={() => setIsAutoSendInterviewInviteEnabled((v) => !v)}
            />
          )}

          <div css={classes.divider} />

          <Checkbox
            label={dictionary.dontShowQualificationQuestionaryPreviewNextTime}
            checked={dontShowQuestionnairePreview ?? false}
            onClick={(value) => setDontShowQuestionnairePreview?.(value)}
          />
          <div css={classes.buttons}>
            <Button
              startIcon={<ChevronLeft size={16} color={(c) => c.neutral.variant[70]} />}
              label={dictionary.back}
              onClick={onGoBack}
              style="text"
              type="subtle2"
            />
            <Button
              label={dictionary.confirmAndStart}
              onClick={() =>
                onConfirm(
                  questionnaireState ?? undefined,
                  isCalendarValid(nylasConnectionStatusQuery.data) && isAutoSendInterviewInviteEnabled,
                )
              }
              style="filled"
              type="success"
              disabled={isLoading}
              startIcon={isLoading ? <Loader size={16} /> : undefined}
            />
          </div>
        </DialogV2.Body>
      </DialogV2.Content>
    </DialogV2.Root>
  );
};
